<template>
  <div v-if="visible" :class="messageClasses" role="alert">
    <!-- Icon -->
    <div class="flex-shrink-0">
      <component :is="iconComponent" :class="iconClasses" />
    </div>
    
    <!-- Content -->
    <div class="flex-1 min-w-0">
      <div v-if="$slots.default" class="message-content">
        <slot></slot>
      </div>
      <div v-else-if="text" class="message-text">{{ text }}</div>
    </div>
    
    <!-- Close button -->
    <button
      v-if="closable"
      @click="close"
      :class="closeButtonClasses"
      type="button"
      aria-label="Close message"
    >
      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
      </svg>
    </button>
  </div>
</template>

<script>
export default {
  name: 'UIMessage',
  emits: ['close'],
  props: {
    severity: {
      type: String,
      default: 'info',
      validator: (value) => ['success', 'info', 'warn', 'error'].includes(value)
    },
    text: {
      type: String,
      default: ''
    },
    closable: {
      type: Boolean,
      default: false
    },
    sticky: {
      type: Boolean,
      default: true
    },
    life: {
      type: Number,
      default: 3000
    }
  },
  data() {
    return {
      visible: true,
      timeout: null
    };
  },
  mounted() {
    if (!this.sticky) {
      this.startTimer();
    }
  },
  beforeUnmount() {
    this.clearTimer();
  },
  computed: {
    messageClasses() {
      const classes = [
        'flex items-start p-4 rounded-md border-l-4 transition-all duration-300'
      ];

      // Severity-based styling
      const severityClasses = {
        success: 'bg-green-50 border-green-400 text-green-800',
        info: 'bg-blue-50 border-blue-400 text-blue-800',
        warn: 'bg-yellow-50 border-yellow-400 text-yellow-800',
        error: 'bg-red-50 border-red-400 text-red-800'
      };
      
      classes.push(severityClasses[this.severity]);
      
      return classes.join(' ');
    },
    iconClasses() {
      const classes = ['w-5 h-5 mr-3 flex-shrink-0'];
      
      const severityIconClasses = {
        success: 'text-green-400',
        info: 'text-blue-400',
        warn: 'text-yellow-400',
        error: 'text-red-400'
      };
      
      classes.push(severityIconClasses[this.severity]);
      
      return classes.join(' ');
    },
    closeButtonClasses() {
      const classes = [
        'ml-3 flex-shrink-0 rounded-md p-1.5 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2'
      ];
      
      const severityCloseClasses = {
        success: 'text-green-500 hover:bg-green-100 focus:ring-green-500',
        info: 'text-blue-500 hover:bg-blue-100 focus:ring-blue-500',
        warn: 'text-yellow-500 hover:bg-yellow-100 focus:ring-yellow-500',
        error: 'text-red-500 hover:bg-red-100 focus:ring-red-500'
      };
      
      classes.push(severityCloseClasses[this.severity]);
      
      return classes.join(' ');
    },
    iconComponent() {
      // Return inline SVG components for each severity
      const icons = {
        success: {
          template: `
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
            </svg>
          `
        },
        info: {
          template: `
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
            </svg>
          `
        },
        warn: {
          template: `
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
            </svg>
          `
        },
        error: {
          template: `
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
            </svg>
          `
        }
      };
      
      return icons[this.severity];
    }
  },
  methods: {
    close() {
      this.visible = false;
      this.$emit('close');
    },
    startTimer() {
      if (this.life > 0) {
        this.timeout = setTimeout(() => {
          this.close();
        }, this.life);
      }
    },
    clearTimer() {
      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }
    }
  }
};
</script> 